import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils"

const kbdStyles = cva(
  "font-sans flex shrink-0 items-center justify-center rounded-[4px] p-1 min-w-5 text-xs font-semibold leading-3",
  {
    variants: {
      variant: {
        default: "bg-gray-900/[0.06] text-gray-900",
        dark: "bg-white/[0.12] text-white"
      }
    },
    defaultVariants: { variant: "default" }
  }
)

type KbdVariant = NonNullable<VariantProps<typeof kbdStyles>["variant"]>

export type KbdProps = React.ComponentPropsWithoutRef<"kbd"> & {
  /**
   * Surface the key is drawn for. `"default"` suits light backgrounds; `"dark"`
   * inverts the fill and text for dark surfaces such as a tooltip popup.
   */
  variant?: KbdVariant
}

/**
 * A single key in a small rounded box — the shared building block for every
 * keyboard affordance in the library. Use it on its own (an `↵` in a hint, a
 * shortcut on a nav trigger) or inside a `KbdGroup` for a chord.
 */
export function Kbd({ variant, className, children, ...props }: KbdProps) {
  return (
    <kbd className={cn(kbdStyles({ variant }), className)} {...props}>
      {children}
    </kbd>
  )
}

Kbd.displayName = "Kbd"

export type KbdGroupProps = React.ComponentPropsWithoutRef<"span">

/**
 * Lays out several `Kbd`s as one chord — e.g. `⌘` `\`.
 */
export function KbdGroup({ className, children, ...props }: KbdGroupProps) {
  return (
    <span
      className={cn("flex shrink-0 items-center gap-1", className)}
      {...props}
    >
      {children}
    </span>
  )
}

KbdGroup.displayName = "KbdGroup"
